import {
    collection, doc, DocumentData, Firestore, getDocsFromServer, query,
    runTransaction, serverTimestamp, where
} from "firebase/firestore";
import {RRLogisticType} from "./RRLogisticType";
import {PLAN_FIELDS, REPORT_FIELDS, transportConflicts, transportErrors, transportMillis, vehicleKey} from "./transportValidation";
import {transportClientChanges, transportVehicleLabel} from "./transportClientChanges";

export type TransportClientMutation = {
    action: "planSave" | "changeDriver" | "reportSave" | "delete";
    logisticId?: string;
    expectedRevision?: number;
    logistic?: RRLogisticType;
    source?: RRLogisticType;
    report?: Partial<RRLogisticType>;
    driver?: {id?: string; name: string};
};
type Actor = {id: string | null; name: string};
const staleMessage = "Записът е променен от друг прозорец или потребител. Презаредете данните и опитайте отново.";
const validId = (value: unknown): value is string => typeof value === "string" && !!value.trim() && !value.includes("/");
const pick = (value: DocumentData, fields: readonly string[]): DocumentData =>
    Object.fromEntries(fields.filter(key => value[key] !== undefined).map(key => [key, value[key]]));

/** Normalize legacy defaults and timestamps solely for comparison, without a data migration. */
function normalized(value: DocumentData): DocumentData {
    const truck = value.truck || {};
    const result: DocumentData = {...value, driverName: value.driverName || "", dayType: value.dayType || "WORKDAY",
        workInStore: value.workInStore ?? false, isOwn: value.isOwn ?? true,
        truck: {id: truck.id || "", name: truck.name || "", plate: truck.plate || "", isOwn: truck.isOwn ?? true}};
    for (const key of ["eventNumber", "startPoint", "endPoint", "planNotes", "reportNotes"]) result[key] = value[key] || "";
    for (const key of ["startKm", "endKm"]) result[key] = value[key] ?? 0;
    for (const key of ["startDate", "endDate", "reportStartDate", "reportEndDate"]) result[key] = transportMillis(value[key]);
    return result;
}
function sameFields(first: DocumentData, second: DocumentData, fields: readonly string[]): boolean {
    return JSON.stringify(pick(normalized(first), fields)) === JSON.stringify(pick(normalized(second), fields));
}

/**
 * Direct web-SDK writes retain existing access rules. Conflict queries are fresh server
 * reads, NOT a serializable query lock: simultaneous creation/legacy clients may bypass
 * them. A document transaction protects its own update and the corresponding audit.
 */
export function createTransportClientWriter(db: Firestore, actorProvider: () => Actor, uidProvider: () => string) {
    const pending = new Map<string, Promise<string>>();
    // Preserve a new document ID for an uncertain retry in this page, without new collections.
    const newIds = new Map<string, string>();
    return function save(mutation: TransportClientMutation): Promise<string> {
        const key = JSON.stringify({uid: uidProvider(), mutation});
        const existing = pending.get(key);
        if (existing) return existing;
        const operation = execute(mutation, key).finally(() => pending.delete(key));
        pending.set(key, operation);
        return operation;
    };

    async function execute(mutation: TransportClientMutation, key: string): Promise<string> {
        const {action} = mutation;
        if (!mutation.logisticId && action !== "planSave") throw new Error("Не е избран транспортен запис.");
        if (mutation.logisticId && !validId(mutation.logisticId)) throw new Error("Невалиден идентификатор на транспортния запис.");
        if (mutation.logisticId && !mutation.source) throw new Error("Липсват оригиналните данни. Презаредете записа.");
        const patch = action === "planSave" ? pick(mutation.logistic || {}, PLAN_FIELDS) :
            action === "reportSave" ? pick(mutation.report || {}, REPORT_FIELDS) :
                action === "changeDriver" ? {driverId: mutation.driver?.id, driverName: mutation.driver?.name} : {};
        if (action === "planSave" && !mutation.logistic) throw new Error("Липсват данни за планиране.");
        const id = mutation.logisticId || newIds.get(key) || doc(collection(db, "logistics")).id;
        if (!mutation.logisticId) newIds.set(key, id);
        const ref = doc(db, "logistics", id);
        const actor = actorProvider();
        const result = await runTransaction(db, async transaction => {
            const snapshot = await transaction.get(ref);
            if (mutation.logisticId && !snapshot.exists()) throw new Error("Записът вече не съществува.");
            const before = snapshot.data() || {};
            if (!mutation.logisticId && snapshot.exists()) {
                if (sameFields(before, patch, PLAN_FIELDS)) return id;
                throw new Error("Данните за повторен запис не съвпадат. Презаредете данните.");
            }
            // Old clients do not increment revision, so compare the original field values too.
            // A plan edit preserves a concurrent report; a report preserves concurrent plan notes.
            const guardedFields = action === "planSave" ? PLAN_FIELDS : action === "reportSave" ?
                [...REPORT_FIELDS, "driverId", "dayType", "truck", "isOwn", "workInStore"] : [...PLAN_FIELDS, ...REPORT_FIELDS];
            // New forms also pass their initial values as source, but there is no persisted
            // original to compare. Only edits of existing documents can be stale.
            if (mutation.logisticId && mutation.source && !sameFields(before, mutation.source, guardedFields)) throw new Error(staleMessage);
            const candidate: DocumentData = {dayType: "WORKDAY", workInStore: false, isOwn: true, eventNumber: "",
                startPoint: "", endPoint: "", planNotes: "", truck: {id: "", name: "", plate: "", isOwn: true},
                reportStartDate: null, reportEndDate: null, startKm: 0, endKm: 0, reportNotes: "",
                ...before, ...patch, id};
            candidate.dayType = candidate.dayType || "WORKDAY";
            let truckRef: ReturnType<typeof doc> | undefined;
            let lastKm = 0;
            if (action !== "delete") {
                const errors = transportErrors(candidate, action === "reportSave" ? "report" : "plan");
                if (errors.length) throw new Error(errors.join("\n"));
                if (!validId(candidate.driverId)) throw new Error("Не е избран шофьор.");
                const driver = await transaction.get(doc(db, "employees", candidate.driverId));
                if (!driver.exists() || (driver.get("isDeleted") === true && candidate.driverId !== before.driverId)) {
                    throw new Error("Избраният шофьор не съществува или е изтрит.");
                }
                if (action !== "reportSave") {
                    patch.driverName = driver.get("name") || candidate.driverName || "";
                    candidate.driverName = patch.driverName;
                }
                if (candidate.dayType === "WORKDAY" && candidate.workInStore !== true) {
                    if (candidate.isOwn !== false) {
                        if (!validId(candidate.truck?.id)) throw new Error("Не е избрано МПС.");
                        truckRef = doc(db, "trucks", candidate.truck.id);
                        const truck = await transaction.get(truckRef);
                        if (!truck.exists()) throw new Error("Избраното МПС не съществува.");
                        if (truck.get("isActive") === false && before.truck?.id !== candidate.truck.id) throw new Error("Избраното МПС не е активно.");
                        lastKm = truck.get("lastKm") || 0;
                    } else if (!candidate.truck?.name?.trim() && !candidate.truck?.plate?.trim()) {
                        throw new Error("Въведете име или регистрация на външния транспорт.");
                    }
                }
                if (candidate.dayType !== "WORKDAY" && (candidate.reportStartDate || candidate.reportEndDate)) {
                    throw new Error("Запис с отчет не може да се преобразува в отпуск или отсъствие.");
                }
                const queries = [query(collection(db, "logistics"), where("driverId", "==", candidate.driverId))];
                if (vehicleKey(candidate)?.startsWith("own:")) queries.push(query(collection(db, "logistics"), where("truck.id", "==", candidate.truck.id)));
                if (vehicleKey(candidate)?.startsWith("external:")) queries.push(query(collection(db, "logistics"), where("isOwn", "==", false)));
                const snapshots = await Promise.all(queries.map(item => getDocsFromServer(item)));
                const matches = Array.from(new Map(snapshots.flatMap(item => item.docs.map(item => [item.id, {...item.data(), id: item.id}] as const))).values());
                errors.push(...transportConflicts(candidate, matches, action === "reportSave" ? "report" : "plan"));
                if (action !== "reportSave" && (candidate.reportStartDate || candidate.reportEndDate)) {
                    errors.push(...transportErrors(candidate, "report"), ...transportConflicts(candidate, matches, "report"));
                }
                if (errors.length) throw new Error(Array.from(new Set(errors)).join("\n"));
            }
            const deletionRef = doc(db, "logisticsDriverChangeLog", `logistics-delete-${id}`);
            const deletion = action === "delete" ? await transaction.get(deletionRef) : null;
            // All transaction reads finish before any writes. No rules or new technical collections.
            const log = (change: {changeType: string; previousValue: string; newValue: string}, deletionLog = false) => {
                transaction.set(deletionLog ? deletionRef : doc(collection(db, "logisticsDriverChangeLog")), {
                    ...change, logisticId: id, eventNumber: candidate.eventNumber || "",
                    periodStart: candidate.startDate || null, periodEnd: candidate.endDate || null,
                    changedAt: serverTimestamp(), changedByEmployeeId: actor.id, changedByName: actor.name
                });
            };
            if (action === "delete") {
                transaction.delete(ref);
                if (!deletion?.exists()) log({changeType: "DELETE", previousValue: `${before.driverName || "Неизвестен служител"}; ${transportVehicleLabel(before)}`, newValue: "Записът е изтрит"}, true);
            } else {
                if (snapshot.exists()) {
                    transportClientChanges(before, candidate, action === "reportSave" ? "report" : "plan").forEach(change => log(change));
                    transaction.update(ref, {...patch, revision: (before.revision ?? 0) + 1});
                } else {
                    const {id: ignoredId, ...data} = candidate;
                    transaction.set(ref, {...data, revision: 1});
                }
                if (action === "reportSave" && truckRef && typeof candidate.endKm === "number" && candidate.endKm > lastKm) {
                    transaction.update(truckRef, {lastKm: candidate.endKm});
                }
            }
            return id;
        });
        newIds.delete(key);
        return result;
    }
}
